import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid,
} from "recharts";
import api from "../lib/api";
import { trackEvent, EVENTS } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../context/AccessContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import PremiumUpgradeModal from "../components/PremiumUpgradeModal";
import {
  Plus, Trash2, Target as TargetIcon, TrendingUp, Award, BarChart3, Sparkles,
  Crown, Lock, BookOpen, Trophy,
} from "lucide-react";
import { toast } from "sonner";

const EXAM_TYPES = ["NEET", "JEE", "Other"];

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const SUBJECT_LABEL = { physics: "Physics", chemistry: "Chemistry", biology: "Biology", maths: "Maths" };

function StatCard({ label, value, sub, icon: Icon, testid, accent = "bg-emerald-500/10 text-emerald-600" }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`grid h-8 w-8 place-items-center rounded-lg ${accent}`}><Icon size={16}/></div>
      </div>
      <div className="mt-2 text-xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-heading text-2xl font-bold">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500">{sub}</div>}
    </div>
  );
}

function Section({ title, subtitle, children, testid }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900" data-testid={testid}>
      <div className="mb-4">
        <h3 className="font-heading text-lg font-semibold">{title}</h3>
        {subtitle && <p className="text-xs text-zinc-500">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function AddTestDialog({ open, onClose, onSaved, onFreeLimitHit }) {
  // Local-date formatter — for our IST-focused audience this is IST midnight.
  // Avoids the classic `.toISOString().slice(0,10)` pitfall which returns
  // the UTC date and would prefill "yesterday" for tests logged 00:00–05:29 IST.
  const _pad = (n) => String(n).padStart(2, "0");
  const _now = new Date();
  const today = `${_now.getFullYear()}-${_pad(_now.getMonth() + 1)}-${_pad(_now.getDate())}`;
  const [form, setForm] = useState({
    name: "", exam_type: "NEET", test_date: today,
    total_score: "", total_max: "720",
    physics: "", chemistry: "", biology: "", maths: "",
    rank: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: "", exam_type: "NEET", test_date: today, total_score: "", total_max: "720", physics: "", chemistry: "", biology: "", maths: "", rank: "", notes: "" });
  }, [open, today]);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        exam_type: form.exam_type,
        test_date: form.test_date,
        total_score: Number(form.total_score),
        total_max: Number(form.total_max),
      };
      for (const k of ["physics", "chemistry", "biology", "maths"]) {
        if (form[k] !== "") payload[k] = Number(form[k]);
      }
      if (form.rank) payload.rank = parseInt(form.rank, 10);
      if (form.notes.trim()) payload.notes = form.notes.trim();
      const { data } = await api.post("/mock-tests", payload);
      trackEvent(EVENTS.MOCK_TEST_CREATED, {
        exam_type: payload.exam_type,
        score_pct: payload.total_max ? Math.round((payload.total_score / payload.total_max) * 100) : null,
      });
      toast.success("Test added");
      onSaved(data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && detail?.code === "free_mock_test_limit") {
        // The pre-flight in the parent already covers most cases, but a race
        // (e.g. cap hit on another tab) can still land us here. Close the
        // form and let the parent open the upgrade modal.
        onClose();
        onFreeLimitHit?.();
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't save test");
      }
    } finally {
      setSaving(false);
    }
  };

  const showBio = form.exam_type === "NEET";
  const showMaths = form.exam_type === "JEE";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="add-test-dialog">
        <DialogHeader>
          <DialogTitle>Add a mock test</DialogTitle>
          <DialogDescription>Track your performance over time.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Test name</label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Aakash AIATS #5" maxLength={80} data-testid="mt-name"/>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Exam type</label>
            <select value={form.exam_type} onChange={(e) => setForm({ ...form, exam_type: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm dark:border-zinc-700 dark:bg-zinc-900" data-testid="mt-exam-type">
              {EXAM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Date</label>
            <Input type="date" required value={form.test_date} onChange={(e) => setForm({ ...form, test_date: e.target.value })} data-testid="mt-date"/>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Total score</label>
            <Input type="number" min="0" step="0.5" required value={form.total_score} onChange={(e) => setForm({ ...form, total_score: e.target.value })} placeholder="540" data-testid="mt-total"/>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Max marks</label>
            <Input type="number" min="1" step="0.5" required value={form.total_max} onChange={(e) => setForm({ ...form, total_max: e.target.value })} placeholder="720" data-testid="mt-max"/>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Physics</label>
            <Input type="number" min="0" step="0.5" value={form.physics} onChange={(e) => setForm({ ...form, physics: e.target.value })} data-testid="mt-physics"/>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Chemistry</label>
            <Input type="number" min="0" step="0.5" value={form.chemistry} onChange={(e) => setForm({ ...form, chemistry: e.target.value })} data-testid="mt-chemistry"/>
          </div>
          {showBio && (
            <div className="col-span-2">
              <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Biology</label>
              <Input type="number" min="0" step="0.5" value={form.biology} onChange={(e) => setForm({ ...form, biology: e.target.value })} data-testid="mt-biology"/>
            </div>
          )}
          {showMaths && (
            <div className="col-span-2">
              <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Maths</label>
              <Input type="number" min="0" step="0.5" value={form.maths} onChange={(e) => setForm({ ...form, maths: e.target.value })} data-testid="mt-maths"/>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Rank (optional)</label>
            <Input type="number" min="1" value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })} placeholder="—" data-testid="mt-rank"/>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Notes (optional)</label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="What went well? What to improve?" maxLength={500} data-testid="mt-notes"/>
          </div>
          <div className="col-span-2 mt-2 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="mt-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="mt-save">{saving ? "Saving…" : "Save test"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PremiumLockedCard({ title, description, testid }) {
  const navigate = useNavigate();
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-emerald-500/10 text-emerald-600"><Lock size={16}/></div>
        <div className="flex-1">
          <h3 className="font-heading text-base font-semibold flex items-center gap-1.5">{title} <Crown size={12} className="text-amber-500"/></h3>
          <p className="mt-0.5 text-xs text-zinc-500">{description}</p>
          <Button onClick={() => navigate("/premium")} size="sm" className="mt-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700" data-testid={`${testid}-unlock`}>
            <Crown size={12} className="mr-1"/> Unlock with Premium
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function MockTests() {
  const { user } = useAuth();
  const { effectivePremium } = useAccess();
  const [tests, setTests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, a] = await Promise.all([
        api.get("/mock-tests"),
        api.get("/mock-tests/analytics"),
      ]);
      setTests(t.data || []);
      setAnalytics(a.data);
    } catch {
      /* analytics tolerates failure */
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    // Pre-flight: if non-premium and at the cap, open the upgrade modal instead.
    if (!effectivePremium && tests.length >= (analytics?.free_limit || 1)) {
      setUpgradeOpen(true);
      return;
    }
    setAddOpen(true);
  };

  // AddTestDialog throws so we can intercept the 403 here
  const handleSaved = (created) => {
    setTests((prev) => [created, ...prev]);
    load();
  };

  const deleteTest = async (id) => {
    if (!window.confirm("Delete this test? This can't be undone.")) return;
    try {
      await api.delete(`/mock-tests/${id}`);
      setTests((prev) => prev.filter((t) => t.test_id !== id));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    }
  };

  const trendData = useMemo(() => (analytics?.trend || []).map((p, i) => ({
    name: p.name?.length > 14 ? p.name.slice(0, 12) + "…" : (p.name || `T${i + 1}`),
    percentage: p.percentage,
    score: p.score,
  })), [analytics]);

  const monthlyData = useMemo(() => (analytics?.monthly_average || []).map((m) => ({
    name: m.month,
    avg: m.average_percentage,
  })), [analytics]);

  const last7 = useMemo(() => (analytics?.last_7 || []).map((p, i) => ({
    name: p.name?.length > 10 ? p.name.slice(0, 9) + "…" : (p.name || `T${i + 1}`),
    score: p.score,
  })), [analytics]);

  return (
    <div className="space-y-6" data-testid="mock-tests-root">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">Performance</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Mock Test Analytics</h1>
          <p className="text-sm text-zinc-500">Track scores · spot trends · stay motivated.</p>
        </div>
        <Button onClick={openAdd} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="add-test-btn">
          <Plus size={14} className="mr-1.5"/> Add test
        </Button>
      </div>

      {/* Empty state */}
      {!loading && tests.length === 0 && (
        <div className="rounded-2xl border border-dashed border-zinc-200 bg-white p-10 text-center dark:border-zinc-800 dark:bg-zinc-900" data-testid="mock-empty-state">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-emerald-500/10 text-emerald-600"><BookOpen size={20}/></div>
          <h3 className="mt-4 font-heading text-lg font-semibold">No tests yet</h3>
          <p className="mt-1 text-sm text-zinc-500">Add your first mock test to see trends, predictions, and insights.</p>
          <Button onClick={openAdd} className="mt-5 bg-emerald-500 text-white hover:bg-emerald-600" data-testid="empty-add-btn">
            <Plus size={14} className="mr-1.5"/> Add your first test
          </Button>
        </div>
      )}

      {tests.length > 0 && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Total tests" value={analytics?.total_tests ?? tests.length} icon={BookOpen} testid="stat-total"/>
            <StatCard label="Latest score" value={analytics?.latest_score ?? "—"} icon={TrendingUp} testid="stat-latest" accent="bg-blue-500/10 text-blue-600"/>
            <StatCard label="Highest score" value={analytics?.highest_score ?? "—"} icon={Trophy} testid="stat-highest" accent="bg-amber-500/10 text-amber-600"/>
            <StatCard label="Average score" value={analytics?.average_score ?? "—"} sub={analytics?.average_percentage != null ? `${analytics.average_percentage}%` : ""} icon={BarChart3} testid="stat-average" accent="bg-purple-500/10 text-purple-600"/>
          </div>

          {/* Trend chart */}
          <Section title="Score trend" subtitle="Percentage across all tests" testid="trend-section">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)"/>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }}/>
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`}/>
                  <Tooltip formatter={(v) => `${v}%`}/>
                  <Line type="monotone" dataKey="percentage" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Section>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Section title="Last 7 tests" subtitle="Score per test" testid="last7-section">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={last7}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)"/>
                    <XAxis dataKey="name" tick={{ fontSize: 11 }}/>
                    <YAxis tick={{ fontSize: 11 }}/>
                    <Tooltip/>
                    <Bar dataKey="score" fill="#10b981" radius={[6, 6, 0, 0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>

            <Section title="Monthly average" subtitle="Average % by month" testid="monthly-section">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)"/>
                    <XAxis dataKey="name" tick={{ fontSize: 11 }}/>
                    <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`}/>
                    <Tooltip formatter={(v) => `${v}%`}/>
                    <Bar dataKey="avg" fill="#3b82f6" radius={[6, 6, 0, 0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {analytics?.monthly_improvement?.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-zinc-600 dark:text-zinc-400">
                  {analytics.monthly_improvement.map((m) => (
                    <li key={m.month}>
                      <span className="font-medium">{m.month}</span> — {m.delta_percent >= 0 ? "+" : ""}{m.delta_percent}% ({m.delta_marks >= 0 ? "+" : ""}{m.delta_marks} marks)
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>

          {/* Premium-only insights — show locked placeholders for free users */}
          {analytics?.is_premium_view ? (
            <>
              {analytics?.insights && (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="insights-grid">
                  {[
                    { k: "strongest", label: "Strongest", icon: Award, accent: "bg-emerald-500/10 text-emerald-600" },
                    { k: "weakest", label: "Weakest", icon: BookOpen, accent: "bg-red-500/10 text-red-600" },
                    { k: "most_improved", label: "Most improved", icon: TrendingUp, accent: "bg-blue-500/10 text-blue-600" },
                    { k: "attention", label: "Needs attention", icon: TargetIcon, accent: "bg-amber-500/10 text-amber-600" },
                  ].map(({ k, label, icon: Icon, accent }) => (
                    <div key={k} className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900" data-testid={`insight-${k}`}>
                      <div className={`grid h-8 w-8 place-items-center rounded-lg ${accent}`}><Icon size={16}/></div>
                      <div className="mt-2 text-xs uppercase tracking-wider text-zinc-500">{label}</div>
                      <div className="font-heading text-lg font-bold capitalize">{analytics.insights[k] ? SUBJECT_LABEL[analytics.insights[k]] : "—"}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {analytics?.prediction && (
                  <Section title="Predicted next score" subtitle={`Based on ${analytics.prediction.samples_used} previous tests`} testid="prediction-card">
                    <div className="flex items-baseline gap-2">
                      <div className="font-heading text-4xl font-extrabold text-emerald-600">{analytics.prediction.predicted_score}</div>
                      <div className="text-sm text-zinc-500">± {analytics.prediction.confidence_band}</div>
                    </div>
                    <div className="mt-1 text-xs text-zinc-500">~{analytics.prediction.predicted_percentage}% predicted</div>
                  </Section>
                )}

                {analytics?.consistency && (
                  <Section title="Consistency" subtitle="0 = highly variable · 100 = rock steady" testid="consistency-card">
                    <div className="flex items-baseline gap-2">
                      <div className="font-heading text-4xl font-extrabold text-blue-600">{analytics.consistency.score}</div>
                      <div className="text-sm text-zinc-500">/ 100</div>
                    </div>
                    <div className="mt-2 inline-flex items-center rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-medium text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">{analytics.consistency.classification}</div>
                  </Section>
                )}
              </div>

              {analytics?.milestones?.length > 0 && (
                <Section title="Milestones" subtitle="Achievements unlocked" testid="milestones-section">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {analytics.milestones.map((m, i) => (
                      <div key={`${m.kind}-${i}`} className="flex items-center gap-3 rounded-xl bg-emerald-500/5 px-3 py-2.5 text-sm" data-testid={`milestone-${m.kind}`}>
                        <Sparkles size={14} className="text-emerald-500 shrink-0"/>
                        <div className="flex-1">
                          <div className="font-medium">{m.label}</div>
                          <div className="text-xs text-zinc-500">{m.test_name} · {m.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="premium-locked-grid">
              <PremiumLockedCard title="Subject insights" description="See your strongest, weakest, most-improved subject." testid="locked-insights"/>
              <PremiumLockedCard title="Score prediction" description="See your predicted next mock-test score." testid="locked-prediction"/>
              <PremiumLockedCard title="Consistency score" description="Get a 0-100 consistency rating with classification." testid="locked-consistency"/>
              <PremiumLockedCard title="Milestones" description="Celebrate firsts, streaks & personal bests." testid="locked-milestones"/>
            </div>
          )}

          {/* All tests list */}
          <Section title="All tests" subtitle={`${tests.length} test${tests.length === 1 ? "" : "s"} logged`} testid="tests-list-section">
            <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {tests.map((t) => (
                <div key={t.test_id} className="flex items-center justify-between gap-3 py-3" data-testid={`test-row-${t.test_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{t.name}</div>
                    <div className="text-xs text-zinc-500">{t.exam_type} · {fmtDate(t.test_date)}{t.rank ? ` · rank ${t.rank}` : ""}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">{t.total_score}<span className="text-xs font-normal text-zinc-500">/{t.total_max}</span></div>
                    <div className="text-xs text-emerald-600">{t.percentage}%</div>
                  </div>
                  <button type="button" onClick={() => deleteTest(t.test_id)} aria-label="Delete test" className="text-zinc-400 hover:text-red-500" data-testid={`delete-${t.test_id}`}>
                    <Trash2 size={14}/>
                  </button>
                </div>
              ))}
            </div>
          </Section>
        </>
      )}

      <AddTestDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={handleSaved}
        onFreeLimitHit={() => setUpgradeOpen(true)}
      />
      <PremiumUpgradeModal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        title="Free mock-test limit reached 🚀"
        description="You've added 1 mock test on the free plan. Upgrade to Premium (₹49/month or ₹299/year) for unlimited tracking + AI insights, score prediction, milestones, and more."
      />
    </div>
  );
}
